import { React, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useMediaQuery } from "react-responsive";
import Slider from "react-slick";
import styled from "@emotion/styled";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import RiverCard from "../Rivers/RiverCard";
import SliderToggles from "../Rivers/SliderToggles";
import useAxios from "../../hooks/useAxios";
import { Colors, Sizes } from "../../styles";

const CardSlider = ({ pageTitle, regionsJson, regionSlugs }) => {
	const [title] = useState(pageTitle ? pageTitle : "");
	const [regions] = useState(regionsJson ? JSON.parse(regionsJson) : []);
	const [endPoint] = useState(
		`rivers?region=${JSON.parse(regionSlugs).join()}`
	);
	const [currentSlides, setCurrentSlides] = useState(0);

	const isMobile = useMediaQuery({
		query: "(max-width: 781px)",
	});

	const sliderRef = useRef();

	const { response: rivers } = useAxios(endPoint);

	const filteredRivers =
		rivers &&
		rivers.length > 0 &&
		rivers.filter((river) => river.title !== title);
	const total = filteredRivers && filteredRivers.length;

	// universal variable that controls number of slides that we want to show
	let numberOfSlides = isMobile ? 1.2 : 4.2;

	const settings = {
		infinite: false,
		dots: false,
		slidesToShow: numberOfSlides,
		slidesToScroll: 1,
		lazyLoad: false,
		autoplay: false,
		autoplaySpeed: 2000,
		arrows: false,
		beforeChange: (current, next) => {
			handleCurrentSlidesChange(current, next);
		},
	};

	useEffect(() => {
		setCurrentSlides(numberOfSlides - 0.2);
	}, [numberOfSlides]);

	const handleCurrentSlidesChange = (current, next) => {
		// right direction
		if (current < next) {
			return setCurrentSlides((prev) => {
				return prev + sliderRef.current.props.slidesToScroll;
			});
		} else {
			// left direction
			return setCurrentSlides((prev) => {
				return prev - sliderRef.current.props.slidesToScroll;
			});
		}
	};

	const renderTitle = () => {
		// formatting title depending on how many tagged regions river has
		if (regions.length > 1) {
			return regions.map((region, index) => {
				if (index === 0) {
					return <span key={region.id}> {region.name}</span>;
				} else {
					return <span key={region.id}> & {region.name} regions</span>;
				}
			});
		}
		return regions.map((region) => {
			// ternary operator accounting for the fact that slider is used on single rivers page (since a river can be part of multiple regions) and single region (since a region is associated to only one region)
			return (
				<span key={region.id}>
					{region.name ? region.name : JSON.parse(regionSlugs)[0]}{" "}
				</span>
			);
		});
	};

	const renderRiversSlider = () => {
		if (filteredRivers) {
			return filteredRivers.map((riverItem) => {
				return (
					<RiverCard isSmall={true} riverItem={riverItem} key={riverItem.id} />
				);
			});
		}
	};

	return (
		<Container>
			<SliderContainer>
				<Title>Explore the rivers of the {renderTitle()}</Title>
				<Slider ref={sliderRef} {...settings}>
					{renderRiversSlider()}
				</Slider>
				{sliderRef.current && total && (
					<SliderToggles
						slidesCount={total}
						// updating the first number if the amount of slides is less than slidesToShow property
						currentSlide={total < numberOfSlides ? total : currentSlides}
						toggleNexSlide={sliderRef.current.slickNext}
						togglePrevSlide={sliderRef.current.slickPrev}
						isMobile={isMobile}
					/>
				)}
			</SliderContainer>
		</Container>
	);
};

export default CardSlider;

CardSlider.propTypes = {
	pageTitle: PropTypes.object,
	regionsJson: PropTypes.any,
	regionSlugs: PropTypes.any,
};

const Container = styled.div`
	margin-top: 4rem;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		margin-top: 6rem;
	}
`;

const SliderContainer = styled.div`
	padding: 11rem 0 26rem 2.4rem;
	width: 100%;
	color: #333;
	position: relative;

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		padding: 12.2rem 0 18.2rem 8.3rem;
	}
`;

const Title = styled.h3`
	font-size: 3rem;
	line-height: 3.3rem;
	margin-bottom: 5rem;
	padding-right: 5rem;
	color: ${Colors.base.white};

	@media only screen and (min-width: ${Sizes.screens.medium}) {
		font-size: 3.6rem;
		line-height: 4.6rem;
		margin-bottom: 4.1rem;
	}
`;
